import React, { FunctionComponent, useState } from 'react';
import { Container } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { compose } from 'recompose';
import { withRouter, match, Link } from 'react-router-dom';
import { Location } from 'history';
import queryString from 'query-string';
import { pathOr, all, propEq, difference } from 'ramda';
import { useQuery } from 'react-apollo';
import classnames from 'classnames';

import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { ClassProp } from 'helpers/types';

import VenueSearchResults from './sections/VenueSearchResults';
import MerchantVenueSearch from 'scenes/Home/components/MerchantVenueSearch';
import {
  Values,
  SearchTypeValues,
  VenueTypeValues,
} from 'scenes/Home/components/MerchantVenueSearch/MerchantVenueSearch.helper';
import LinkConfirm from 'components/LinkConfirm';
import { useMutation } from 'helpers/useMutation';
import StoreListUploadModal from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantVenues/sections/StoreListUploadModal';

import TOGGLE_VENUE_TRANSACTION_FLOW_BY_MERCHANT from './MerchantVenues.mutations';
import CreateOnlineVenueModal from './sections/CreateOnlineVenueModal/CreateOnlineVenueModal';

const styles = {
  container: {
    margin: '1.25rem auto !important',
    height: '100% !important',
  },
  onboardButton: {
    backgroundColor: '#683caf !important',
    borderRadius: '1.5rem !important',
    color: '#fff !important',
    fontSize: '.8rem',
    marginTop: '1rem',
    float: 'right',
    opacity: 1,
    padding: '0.5rem 1.25rem !important',
    fontWeight: '500 !important',
    lineHeight: '1.25rem !important',
    cursor: 'pointer !important',
    marginLeft: '1rem !important',
    border: 'none',

    '&:hover': {
      opacity: 0.9,
    },
    '&:active': {
      opacity: 0.7,
    },
  },
  onboardVenuesSection: {
    display: 'inline-block',
    width: '100%',
  },
  disabled: {
    backgroundColor: '#d8dee1 !important',
    color: '#f7f8f9 !important',
    pointerEvents: 'none !important',
  },
};

const getAllVenuesActive = all(propEq('status', 'active'));

const getLinkConfirmMessage = (allVenuesActive: boolean) =>
  `We're going to turn ${
    allVenuesActive ? 'off' : 'on'
  } the venue transactions. Are you sure?`;

type Props = {
  classes: ClassProp<typeof styles>;
  location: Location;
  match: match;
};

const MerchantVenues: FunctionComponent<Props> = ({
  classes,
  location,
  match,
}) => {
  const [venues, setVenues] = useState<Record<string, unknown>[]>([]);
  const [venuesLoading, setVenuesLoading] = useState<boolean>(true);
  const [allVenuesActive, setAllVenuesActive] = useState<boolean>(false);
  const { isInternalUser, isTier3 } = useUserMgmt();
  const { data } = useQuery(GET_PROFILE);

  const { userProfile } = data;

  const merchantId = pathOr(
    userProfile.merchantId,
    ['params', 'merchantId'],
    match,
  );

  const { runMutation, loading: loadingMutation } = useMutation(
    TOGGLE_VENUE_TRANSACTION_FLOW_BY_MERCHANT,
    {
      params: {
        mode: allVenuesActive ? 'TURN_OFF' : 'TURN_ON',
        merchantUuid: merchantId,
      },
    },
    `It has been successfully processed.`,
  );

  const loading = venuesLoading || loadingMutation;

  const {
    searchType = SearchTypeValues.None,
    searchText = '',
    cnidsToFilter = '',
    venueType = VenueTypeValues.All,
    filter = '',
  }: Values = queryString.parse(location.search);

  const onCompleted = (data: any) => {
    if (difference(data, venues).length > 0) {
      setVenuesLoading(false);
      setVenues(data);
      setAllVenuesActive(getAllVenuesActive(data));
    }
  };

  const onConfirmed = () => {
    runMutation();
    setAllVenuesActive(!allVenuesActive);
  };

  return (
    <Container className={classes.container}>
      <MerchantVenueSearch
        path={match.url}
        requireSearch={false}
        showFilters={isInternalUser}
        showVMID={false}
        isInternalUser={isInternalUser}
      />
      {(isInternalUser || isTier3) && (
        <div className={classes.onboardVenuesSection}>
          <LinkConfirm
            redirect={false}
            onConfirmed={onConfirmed}
            content={getLinkConfirmMessage(allVenuesActive)}
            className={classnames(classes.onboardButton, {
              [classes.disabled]: loading,
            })}
          >
            {loading
              ? 'Loading...'
              : `Turn ${allVenuesActive ? 'Off' : 'On'} Transactions`}
          </LinkConfirm>

          <Link
            className={classes.onboardButton}
            to={{
              pathname: `${match.url}/management`,
            }}
          >
            Venue Management
          </Link>

          <StoreListUploadModal
            title="Upload Merchant Store List"
            trigger={
              <button className={classes.onboardButton}>
                Upload Store List
              </button>
            }
          />
          <CreateOnlineVenueModal
            trigger={
              <button className={classes.onboardButton}>
                Create Online Venue
              </button>
            }
          />
        </div>
      )}

      <VenueSearchResults
        onCompleted={onCompleted}
        searchType={searchType}
        searchText={searchText}
        filter={filter}
        cnidsToFilter={cnidsToFilter}
        merchantId={merchantId}
        venueType={venueType}
      />
    </Container>
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(MerchantVenues);
